import { useMemo } from 'react';

import { useFormContext } from 'react-hook-form';
import type {
	InputActionMeta,
	MultiValue,
	OnChangeValue,
	SingleValue
} from 'react-select';

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import {
	InputSelect,
	type InputSelectProps
} from '@/components/ui/input-select';
import { cn } from '@/lib/utils';

type TSelectControl<
	OptionType extends BaseSelectOption,
	IsMulti extends boolean = false
> = {
	name: string,
	label?: string,
	type?: string,
	className?: string,
	placeholder?: string,
	onInputChange?:
		| ((_newValue: string, _actionMeta: InputActionMeta) => void)
		| undefined
		| null,
	setter?: (
		selected: NonNullable<
			IsMulti extends true ? MultiValue<OptionType> : SingleValue<OptionType>
		>
	) => void,
	hint?: React.ReactNode,
	options: readonly OptionType[],
	isMulti?: IsMulti
} & Partial<
	StrictOmit<
		InputSelectProps<OptionType, IsMulti>,
		'options' | 'value' | 'isMulti'
	>
>;

export type BaseSelectOption = {
	label: string,
	value: string | number,
	isFixed?: boolean,
	selected?: boolean,
	options?: readonly BaseSelectOption[]
};

const SelectControl = <
	OptionType extends BaseSelectOption,
	IsMulti extends boolean = false
>({
	name,
	label,
	placeholder,
	onInputChange,
	setter,
	hint,
	className,
	options,
	isMulti,
	...props
}: TSelectControl<OptionType, IsMulti>) => {
	const { control } = useFormContext();

	const optionsMap = useMemo(() => {
		const flatOptions = options.flatMap(
			(opt) => (opt.options || [opt]) as readonly OptionType[]
		);
		return new Map(flatOptions.map((opt) => [opt.value, opt]));
	}, [options]);

	function getOptionFromValue(
		value: OneOrMany<string | number>,
		isMulti = false
	): OptionType[] | OptionType | null {
		if (isMulti) {
			if (!Array.isArray(value)) return [];

			return value
				.map((val) => optionsMap.get(val))
				.filter((item): item is OptionType => Boolean(item));
		}

		if (typeof value !== 'string' && typeof value !== 'number') {
			return null;
		}
		return optionsMap.get(value) ?? null;
	}

	return (
		<FormField
			control={control}
			name={name}
			defaultValue=""
			render={({
				field: { onChange, value, onBlur },
				formState: { disabled },
				fieldState: { error }
			}) => (
				<FormItem className={cn('flex w-full flex-col gap-2', className)}>
					{label ? (
						<FormLabel className="font-medium" htmlFor={undefined}>
							{label}
						</FormLabel>
					) : null}
					<FormControl id={undefined}>
						<div className="flex w-full flex-col">
							<InputSelect
								name={name}
								placeholder={placeholder || 'Pilih ' + label}
								onInputChange={onInputChange}
								onBlur={onBlur}
								value={getOptionFromValue(value, isMulti)}
								error={!!error}
								isMulti={isMulti}
								options={options}
								filterOption={(opt, inp) =>
									opt.label.toLowerCase().includes(inp.toLowerCase())
								}
								{...props}
								isDisabled={disabled || props.isDisabled}
								onChange={(selected: OnChangeValue<OptionType, IsMulti>) => {
									if (Array.isArray(selected)) {
										onChange(selected.map((opt) => (opt as OptionType).value));
									} else if (selected) {
										onChange((selected as OptionType).value);
									} else {
										onChange(null);
									}

									if (setter && selected) {
										setter(selected);
									}
								}}
							/>
							<FormMessage className="mt-0" />
							{hint && <FormDescription className="mt-1">{hint}</FormDescription>}
						</div>
					</FormControl>
				</FormItem>
			)}
		/>
	);
};

export default SelectControl;
